// src/pages/ActivityLogPage.jsx
// Unified audit log page — reads from Firestore via GET /api/logs/activity.
// Accessible to all roles except members (member portal is separate).
// Logs are permanent — no delete/clear functionality.
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SOURCE_LABELS } from '../utils/activityLog';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function fmt(n, dp = 2) {
  const v = Number(n);
  if (isNaN(v)) return n ?? '—';
  return v.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

function sourceLabel(source) {
  return SOURCE_LABELS[source] || source || '—';
}

const CORE_FIELDS = new Set(['id', 'timestamp', 'source', 'changedBy', 'changedByName', 'changedByUid', 'memberId', 'memberName', 'createdAt', 'action', 'targetType', 'targetId', 'changes']);
const SKIP_DETAIL_FIELDS = new Set(['memberDocId', 'deletedShareId']);
const WHITELIST_ACTIONS = new Set(['devtools_access_granted', 'devtools_access_revoked']);

// Convert camelCase / snake_case / prefixed keys to readable labels
function humanKey(k) {
  return k
    .replace(/^(deleted_|updated_)/, '') // strip flattening prefixes
    .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase → words
    .replace(/_/g, ' ')
    .toLowerCase();
}

function buildDetails(log) {
  const parts = [];
  for (const [k, v] of Object.entries(log)) {
    if (CORE_FIELDS.has(k) || SKIP_DETAIL_FIELDS.has(k)) continue;
    if (v === undefined || v === null || v === '') continue;
    if (typeof v === 'object') continue; // skip any remaining nested objects
    const isCurrencyKey = /amount|balance|total/i.test(k);
    const display = typeof v === 'number'
      ? (isCurrencyKey ? fmt(v) : String(v))
      : String(v);
    parts.push(`${humanKey(k)}: ${display}`);
  }
  return parts.join(' · ') || '—';
}

export default function ActivityLogPage() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [filterSource, setFilterSource] = useState('');
  const [filterMember, setFilterMember] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterMonth, setFilterMonth] = useState('');

  useEffect(() => { loadLogs(); }, []);

  async function loadLogs() {
    try {
      setLoading(true);
      setErr('');
      const token = localStorage.getItem('hopeSaccoToken');
      const params = new URLSearchParams({ limit: 1000 });
      if (filterSource) params.set('source', filterSource);
      if (filterMember) params.set('memberId', filterMember);

      const r = await fetch(`${API_URL}/logs/activity?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const j = await r.json();
      if (!j.success) throw new Error(j.message || 'Failed to load logs');
      setLogs(j.data || []);
    } catch (e) {
      setErr(e.message || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  }

  const uniqueSources = useMemo(() => [...new Set(logs.map(l => l.source).filter(Boolean))].sort(), [logs]);
  const uniqueMonths = useMemo(() => {
    const months = new Set();
    logs.forEach(l => {
      if (l.month) months.add(l.month);
      if (l.disbursementDate) months.add(String(l.disbursementDate).slice(0, 7));
    });
    return [...months].sort().reverse();
  }, [logs]);

  const filtered = useMemo(() => {
    return logs.filter(log => {
      if (WHITELIST_ACTIONS.has(log.action)) return false; // hide DevTools access management entries
      if (filterSource && log.source !== filterSource) return false;
      if (filterMember) {
        const t = filterMember.toLowerCase();
        if (!(log.memberId || '').toLowerCase().includes(t) && !(log.memberName || '').toLowerCase().includes(t)) return false;
      }
      if (filterUser) {
        const t = filterUser.toLowerCase();
        if (!(log.changedBy || '').toLowerCase().includes(t) && !(log.changedByName || '').toLowerCase().includes(t)) return false;
      }
      if (filterMonth) {
        const inMonth = log.month === filterMonth ||
          (log.disbursementDate && String(log.disbursementDate).startsWith(filterMonth));
        if (!inMonth) return false;
      }
      return true;
    });
  }, [logs, filterSource, filterMember, filterUser, filterMonth]);

  function handleExportCSV() {
    if (!filtered.length) return;
    const headers = ['Timestamp', 'Source', 'Member ID', 'Member Name', 'Details', 'Changed By'];
    const rows = filtered.map(log => [
      log.timestamp,
      sourceLabel(log.source),
      log.memberId || '',
      log.memberName || '',
      buildDetails(log),
      log.changedBy || '',
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity-log${filterSource ? '-' + filterSource : ''}${filterMonth ? '-' + filterMonth : ''}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const hasFilters = filterSource || filterMember || filterUser || filterMonth;

  return (
    <div style={{ padding: '20px', maxWidth: '100%', background: '#f5f5f5', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ color: '#4b3ff6', fontSize: '32px', fontWeight: 'bold', marginBottom: '10px' }}>
            Activity Log
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#666' }}>
            <span onClick={() => navigate('/admin/dashboard')} style={{ cursor: 'pointer', color: '#4b3ff6' }}>Dashboard</span>
            <span>›</span>
            <span style={{ color: '#333' }}>Activity Log</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={loadLogs} disabled={loading} style={{ padding: '8px 16px', fontSize: '14px' }}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <button onClick={handleExportCSV} disabled={!filtered.length} style={{ padding: '8px 16px', fontSize: '14px' }}>
            Export CSV
          </button>
        </div>
      </div>

      <div className="container" style={{ maxWidth: '98%', background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>

        {err && <div className="alert alert-danger" style={{ marginBottom: 12 }}>{err}</div>}

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 'bold', marginRight: 6 }}>Source:</label>
            <select
              value={filterSource}
              onChange={e => setFilterSource(e.target.value)}
              style={{ padding: '6px 10px', fontSize: 13, borderRadius: 4, border: '1px solid #ccc' }}
            >
              <option value="">All sources</option>
              {uniqueSources.map(s => <option key={s} value={s}>{sourceLabel(s)}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 'bold', marginRight: 6 }}>Month:</label>
            <select
              value={filterMonth}
              onChange={e => setFilterMonth(e.target.value)}
              style={{ padding: '6px 10px', fontSize: 13, borderRadius: 4, border: '1px solid #ccc' }}
            >
              <option value="">All months</option>
              {uniqueMonths.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 'bold', marginRight: 6 }}>Member:</label>
            <input
              type="text" placeholder="ID or name" value={filterMember}
              onChange={e => setFilterMember(e.target.value)}
              style={{ padding: '6px 10px', fontSize: 13, borderRadius: 4, border: '1px solid #ccc', width: 180 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 'bold', marginRight: 6 }}>Changed By:</label>
            <input
              type="text" placeholder="Email or name" value={filterUser}
              onChange={e => setFilterUser(e.target.value)}
              style={{ padding: '6px 10px', fontSize: 13, borderRadius: 4, border: '1px solid #ccc', width: 200 }}
            />
          </div>
          <span style={{ fontSize: 13, color: '#666' }}>
            {filtered.length} record{filtered.length !== 1 ? 's' : ''}
            {logs.length !== filtered.length ? ` (of ${logs.length} total)` : ''}
          </span>
          {hasFilters && (
            <button
              onClick={() => { setFilterSource(''); setFilterMember(''); setFilterUser(''); setFilterMonth(''); }}
              style={{ padding: '4px 10px', fontSize: 12, borderRadius: 4 }}
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading logs...</div>
        ) : logs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
            <div style={{ fontSize: 16 }}>No activity logs yet.</div>
            <div style={{ fontSize: 13, marginTop: 6 }}>Logs appear here whenever a loan is applied, imported, or a repayment is adjusted.</div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>No records match the current filters.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
              <table className="table table-striped" style={{ fontSize: '13px', width: '100%' }}>
                <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
                  <tr>
                    <th style={{ minWidth: 155 }}>Timestamp</th>
                    <th style={{ minWidth: 210 }}>Source</th>
                    <th style={{ minWidth: 90 }}>Member ID</th>
                    <th style={{ minWidth: 150 }}>Member Name</th>
                    <th style={{ minWidth: 400 }}>Details</th>
                    <th style={{ minWidth: 200 }}>Changed By</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((log, i) => (
                    <tr key={log.id || i}>
                      <td style={{ fontSize: 12, color: '#555', whiteSpace: 'nowrap' }}>
                        {new Date(log.timestamp).toLocaleString('en-KE', {
                          year: 'numeric', month: 'short', day: '2-digit',
                          hour: '2-digit', minute: '2-digit', second: '2-digit',
                        })}
                      </td>
                      <td>
                        <span style={{
                          fontSize: 11, padding: '2px 8px', borderRadius: 12, fontWeight: 'bold',
                          background: log.source?.startsWith('monthly') ? '#e8f4f8'
                            : log.source?.startsWith('apply') ? '#e8f8e8'
                            : log.source?.startsWith('historical') ? '#fff3cd'
                            : '#f0f0f0',
                          color: log.source?.startsWith('monthly') ? '#0c5460'
                            : log.source?.startsWith('apply') ? '#155724'
                            : log.source?.startsWith('historical') ? '#856404'
                            : '#333',
                        }}>
                          {sourceLabel(log.source)}
                        </span>
                      </td>
                      <td><strong>{log.memberId || '—'}</strong></td>
                      <td>{log.memberName || '—'}</td>
                      <td style={{ fontSize: 12, color: '#444', maxWidth: 420 }}>{buildDetails(log)}</td>
                      <td style={{ fontSize: 12, color: '#555' }}>
                        {log.changedByName ? `${log.changedByName} (${log.changedBy})` : log.changedBy || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
